import React, { useMemo } from 'react';
import { ResponsiveContainer, PieChart, Pie, Tooltip, Cell } from 'recharts';

import { bigNumberToText, getAuthsByAuthTypeData, getAuthTypeProtocols } from '../utils';
import { AUTH_TYPES_TO_SHOW, FEATURE_FLAGS_NAMES } from '@silverfort-nodelibs/common/utils/consts';

import smallStyles from './AuthsPie.module.css';
import largeStyle from './AuthsPieLarge.module.css';
import useFeature from '../SystemSettings/hooks/featureFlags/useFeature';
import { SYSTEM_MODES } from '@silverfort-nodelibs/common/utils/system-mode-config';

const PieCustomTooltip = ({ active, payload }) => {
    const styles = smallStyles;
    if (!active || !payload || payload.length === 0) {
        return <div></div>;
    }

    return (
        <div className={styles.customTooltip}>
            <div className={styles.day}>{`${payload[0].payload.protocol}`}</div>
            <div className={styles.count}>
                {Number(payload[0].payload.authentications).toLocaleString()}
                <span>Authentications</span>
            </div>
        </div>
    );
};

const AuthsPie = ({ protocolUsages, totalAuths, large }) => {
    const systemMode = useFeature(FEATURE_FLAGS_NAMES.SystemMode);
    const styles = large ? largeStyle : smallStyles;
    const protocolsData = [],
        usagesData = {};
    for (const protocolUsage of protocolUsages) {
        protocolsData[protocolUsage.protocol_type.toUpperCase()] = protocolUsage.count;
        totalAuths += protocolUsage.count;
    }

    const authTypeToHide =
        systemMode !== SYSTEM_MODES.ON_PREM ? [AUTH_TYPES_TO_SHOW.RADIUS, AUTH_TYPES_TO_SHOW.PING_ADAPTER] : [];

    const authTypeToShow = useMemo(
        () =>
            Object.keys(AUTH_TYPES_TO_SHOW).filter(
                (authType) => !authTypeToHide.includes(AUTH_TYPES_TO_SHOW[authType])
            ),
        []
    );

    for (const authType of Object.keys(AUTH_TYPES_TO_SHOW)) {
        let usageData = 0;
        const protocols = getAuthTypeProtocols(authType);
        if (protocols.length > 0) {
            for (const protocol of protocols) {
                if (protocolsData[protocol]) {
                    usageData += protocolsData[protocol];
                }
            }
        } else if (protocolsData[authType]) {
            usageData = protocolsData[authType];
        }
        usagesData[authType.toUpperCase()] = usageData;
    }
    const { authsByProtocolData, authsByProtocolDataColors } = getAuthsByAuthTypeData(usagesData, totalAuths);

    return (
        <div className={styles.authsByAuthTypesPie}>
            <div className={styles.legend}>
                {authTypeToShow.map((AuthType) => {
                    const label = AUTH_TYPES_TO_SHOW[AuthType].short_label || AUTH_TYPES_TO_SHOW[AuthType].label;
                    return (
                        <div
                            key={`legend_${label}`}
                            className={styles.part}
                            title={`${label} (${usagesData ? bigNumberToText(usagesData[AuthType]) : 0})`}>
                            <i className={['fa fa-circle', label.replace(' ', '_').toLowerCase()].join(' ')}></i>
                            <div>{label}</div>
                            <span>{`(${usagesData ? bigNumberToText(usagesData[AuthType]) : 0})`}</span>
                        </div>
                    );
                })}
            </div>
            <div className={styles.pie}>
                <ResponsiveContainer width="100%">
                    <PieChart>
                        <Pie
                            data={authsByProtocolData}
                            margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                            dataKey="authentications"
                            innerRadius="70%"
                            outerRadius="90%"
                            fill="#8884d8"
                            paddingAngle={0}
                            stroke="none">
                            {authsByProtocolData.map((entry, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={authsByProtocolDataColors[index % authsByProtocolDataColors.length]}
                                />
                            ))}
                        </Pie>
                        <Tooltip cursor={false} position={{ x: -130 }} content={<PieCustomTooltip />} />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default React.memo(AuthsPie);
